/**
 * /settings → Teams (admin-only).
 *
 * Beheer-UI voor teams: lijst, aanmaken, lid toevoegen/verwijderen, en
 * verwijderen. Het auto-aangemaakte **Admin-team** verschijnt met
 * paarse badge — leden hiervan krijgen automatisch `role='admin'` op
 * de workspace. Bewerken/verwijderen van het Admin-team is uitgeschakeld;
 * member-add/remove werkt zoals bij andere teams en triggert role-sync
 * server-side (zie M6.5 + `db/teams.ts`).
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type TeamDTO } from "../api/client";

interface Props {
  workspaceId: string;
}

interface Member {
  teamId: string;
  userEmail: string;
  addedBy?: string;
  addedAt: string;
}

export function TeamsSettings({ workspaceId }: Props) {
  const { t } = useTranslation();
  const [teams, setTeams] = useState<TeamDTO[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [draftName, setDraftName] = useState("");
  const [draftFounder, setDraftFounder] = useState("");
  const [draftDescription, setDraftDescription] = useState("");

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await api.listTeams(workspaceId);
      setTeams(res.teams);
    } catch (err) {
      setError(explain(err));
    }
  }, [workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = async () => {
    const name = draftName.trim();
    const founder = draftFounder.trim().toLowerCase();
    if (!name || !founder) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const body: { name: string; description?: string | null; foundingMember: string } = {
        name,
        foundingMember: founder,
      };
      const desc = draftDescription.trim();
      if (desc) body.description = desc;
      const created = await api.createTeam(workspaceId, body);
      setInfo(t("settings.teams.createdToast", { name: created.name }));
      setDraftName("");
      setDraftFounder("");
      setDraftDescription("");
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (team: TeamDTO) => {
    if (!confirm(t("settings.teams.deleteConfirm", { name: team.name }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await api.deleteTeam(workspaceId, team.id);
      setInfo(t("settings.teams.deletedToast", { name: team.name }));
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <section className="rounded-lg border border-(--ld-border) bg-(--ld-surface) p-5">
        <h2 className="font-semibold text-base text-(--ld-text)">
          {t("settings.teams.newTeamTitle")}
        </h2>
        <p className="mt-1 text-(--ld-muted) text-sm">{t("settings.teams.newTeamDescription")}</p>
        <div className="mt-4 flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-(--ld-text)">{t("settings.teams.nameLabel")}</span>
            <input
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-2 text-sm focus:border-(--ld-accent) focus:outline-none"
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder={t("settings.teams.namePlaceholder")}
              disabled={busy}
              maxLength={120}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-(--ld-text)">{t("settings.teams.founderLabel")}</span>
            <input
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-2 text-sm focus:border-(--ld-accent) focus:outline-none"
              type="email"
              value={draftFounder}
              onChange={(e) => setDraftFounder(e.target.value)}
              placeholder={t("settings.teams.founderPlaceholder")}
              disabled={busy}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-(--ld-text)">{t("settings.teams.descriptionLabel")}</span>
            <textarea
              className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-2 text-sm focus:border-(--ld-accent) focus:outline-none"
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              disabled={busy}
              rows={2}
            />
          </label>
          <div>
            <button
              type="button"
              onClick={() => void create()}
              disabled={busy || !draftName.trim() || !draftFounder.trim()}
              className="rounded bg-(--ld-primary) px-3 py-1.5 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
            >
              {t("settings.teams.createButton")}
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-(--ld-border) bg-(--ld-surface) p-5">
        <h2 className="font-semibold text-base text-(--ld-text)">
          {t("settings.teams.listTitle")}
        </h2>
        <p className="mt-1 text-(--ld-muted) text-sm">
          {t("settings.teams.listDescriptionBefore")}
          <strong className="text-(--ld-text)">
            {t("settings.teams.listDescriptionAdminTeam")}
          </strong>
          {t("settings.teams.listDescriptionAfter")}
        </p>
        {error && (
          <p className="mt-3 rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
            {error}
          </p>
        )}
        {info && (
          <p className="mt-3 rounded border border-transparent bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm">
            {info}
          </p>
        )}
        {teams.length === 0 ? (
          <p className="mt-4 text-(--ld-muted) text-sm italic">{t("settings.teams.empty")}</p>
        ) : (
          <ul className="mt-4 flex flex-col gap-2">
            {teams.map((t) => (
              <TeamRow
                key={t.id}
                team={t}
                workspaceId={workspaceId}
                onDelete={() => void remove(t)}
                onChange={() => void refresh()}
                busy={busy}
                setBusy={setBusy}
                setError={setError}
                setInfo={setInfo}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function TeamRow({
  team,
  workspaceId,
  onDelete,
  onChange,
  busy,
  setBusy,
  setError,
  setInfo,
}: {
  team: TeamDTO;
  workspaceId: string;
  onDelete: () => void;
  onChange: () => void;
  busy: boolean;
  setBusy: (v: boolean) => void;
  setError: (v: string | null) => void;
  setInfo: (v: string | null) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [draftEmail, setDraftEmail] = useState("");

  const loadMembers = useCallback(async () => {
    try {
      const res = await api.getTeam(workspaceId, team.id);
      setMembers(res.members);
    } catch (err) {
      setError(explain(err));
    }
  }, [workspaceId, team.id, setError]);

  useEffect(() => {
    if (expanded) void loadMembers();
  }, [expanded, loadMembers]);

  const addMember = async () => {
    const email = draftEmail.trim().toLowerCase();
    if (!email) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await api.addTeamMember(workspaceId, team.id, email);
      setInfo(
        team.isAdmin
          ? t("settings.teams.addedAdmin", { email })
          : t("settings.teams.addedRegular", { email, name: team.name }),
      );
      setDraftEmail("");
      await loadMembers();
      onChange();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (email: string) => {
    if (!confirm(t("settings.teams.removeConfirm", { email, name: team.name }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await api.removeTeamMember(workspaceId, team.id, email);
      setInfo(
        team.isAdmin
          ? t("settings.teams.removedAdmin", { email })
          : t("settings.teams.removedRegular", { email }),
      );
      await loadMembers();
      onChange();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className={`rounded border px-3 py-2 ${
        team.isAdmin
          ? "border-(--ld-border) bg-(--ld-raised)"
          : "border-(--ld-control) bg-(--ld-surface)"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="font-medium text-(--ld-text) hover:underline"
            >
              {expanded ? "▾" : "▸"} {team.name}
            </button>
            {team.isAdmin && (
              <span
                className="rounded bg-(--ld-active) px-1.5 py-0.5 text-xs text-(--ld-text)"
                title={t("settings.teams.adminBadgeTitle")}
              >
                {t("settings.teams.adminBadge")}
              </span>
            )}
          </div>
          {team.description && (
            <p className="mt-0.5 text-(--ld-muted) text-xs">{team.description}</p>
          )}
        </div>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-(--ld-text) text-xs hover:bg-(--ld-raised)"
            title={
              expanded
                ? t("settings.teams.toggleMembersTitleHide")
                : t("settings.teams.toggleMembersTitleShow")
            }
          >
            {expanded
              ? t("settings.teams.toggleMembersHide")
              : t("settings.teams.toggleMembersShow")}
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={busy || team.isAdmin}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-(--ld-negative) text-xs hover:bg-(--ld-negative-bg) disabled:opacity-40"
            title={
              team.isAdmin
                ? t("settings.teams.deleteTitleAdmin")
                : t("settings.teams.deleteTitleOther")
            }
          >
            {t("settings.teams.deleteButton")}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 border-(--ld-border) border-t pt-3">
          <div className="flex flex-col gap-2">
            <div className="text-(--ld-text) text-xs">
              {members.length === 0 ? (
                <span className="italic text-(--ld-muted)">{t("settings.teams.noMembers")}</span>
              ) : (
                <ul className="flex flex-col gap-1">
                  {members.map((m) => (
                    <li
                      key={m.userEmail}
                      className="flex items-center justify-between rounded border border-(--ld-border) bg-(--ld-surface) px-2 py-1"
                    >
                      <span>{m.userEmail}</span>
                      <button
                        type="button"
                        onClick={() => void removeMember(m.userEmail)}
                        disabled={busy}
                        className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-negative) text-xs hover:bg-(--ld-negative-bg) disabled:opacity-40"
                      >
                        {t("settings.teams.memberRemove")}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex gap-2">
              <input
                className="flex-1 rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
                type="email"
                value={draftEmail}
                onChange={(e) => setDraftEmail(e.target.value)}
                placeholder={t("settings.teams.memberEmailPlaceholder")}
                disabled={busy}
              />
              <button
                type="button"
                onClick={() => void addMember()}
                disabled={busy || !draftEmail.trim()}
                className="rounded bg-(--ld-primary) px-3 py-1 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
              >
                {t("settings.teams.addMember")}
              </button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? `HTTP ${err.status}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}
